import { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import {
  PriceBookDetail,
  PricebookBranches,
} from "../../../../types/price-book.type";
import { numberWithCommas } from "../../../../utils";
import { useLeavePageGuard } from "../../../../utils/hooks/useLeavePageGuard";
import { notifyPricePeriodsChanged } from "../../../../utils/hooks/useExpiringPricePeriodCount";
import ConfirmModal from "../../../ConfirmModal";
import {
  PRICE_BOOK_PERIODS_TAB,
  PRICE_BOOK_SCHEDULES_TAB,
  PRICE_BOOK_TAB_PARAM,
} from "../../shared/constants";
import PeriodDialog from "../Periods/PeriodDialog";
import ScheduleBar from "../Schedules/ScheduleBar";
import { PeriodLine, toPeriodLine } from "../Periods/periodModel";
import { usePeriodDialog } from "../Periods/usePeriodDialog";
import BranchDialog from "./BranchDialog";
import BranchShopsTab from "./BranchShopsTab";
import ConfirmPriceDialog from "./ConfirmPriceDialog";
import DraftBar from "./DraftBar";
import HistoryTab from "./HistoryTab";
import PeriodsTab from "./PeriodsTab";
import SchedulesTab from "./SchedulesTab";
import { LinePeriodActions } from "./ProductLineRow";
import PricebookProductTable from "./ProductTable";
import SummaryCards from "./SummaryCards";
import UnsavedChangesBar from "./UnsavedChangesBar";
import { buildPriceImpactMessage } from "./branchSelection";
import { ReservedBookKind } from "./reservedBook";
import { usePricebookBranches } from "./usePricebookBranches";
import { PricebookDraftDiffState } from "./usePricebookDraftDiff";
import { usePricebookEditor } from "./usePricebookEditor";
import { usePricebookPriceSave } from "./usePricebookPriceSave";

type DetailsTab =
  | "products"
  | typeof PRICE_BOOK_PERIODS_TAB
  | typeof PRICE_BOOK_SCHEDULES_TAB
  | "shops"
  | "history";

const DETAILS_TABS: DetailsTab[] = [
  "products",
  PRICE_BOOK_PERIODS_TAB,
  PRICE_BOOK_SCHEDULES_TAB,
  "shops",
  "history",
];

const toDetailsTab = (value: string | null): DetailsTab =>
  DETAILS_TABS.find((tab) => tab === value) ?? "products";

/** A tab the book does not have (periods on the face-price book, schedules on a draft) falls back to its products. */
const resolveActiveTab = (
  tab: DetailsTab,
  takesPeriods: boolean,
  takesSchedules: boolean,
): DetailsTab => {
  if (tab === PRICE_BOOK_PERIODS_TAB && !takesPeriods) return "products";
  if (tab === PRICE_BOOK_SCHEDULES_TAB && !takesSchedules) return "products";
  return tab;
};

/**
 * A period write reloads the lines to refresh their chips, which would drop price edits nobody
 * saved, so the periods wait for those edits to be saved or discarded.
 */
const PERIOD_BLOCKED_BY_EDITS =
  "บันทึกหรือยกเลิกการแก้ไขราคาก่อน จึงจะตั้งช่วงราคาได้";
/** The dialog prices a period from the stored lines, so writes wait until they are loaded. */
const PERIOD_WAITING_FOR_LINES =
  "ต้องโหลดรายการสินค้าให้ครบก่อน จึงจะตั้งช่วงราคาได้";

/** Why no period can be written right now, or "" when one can. */
const resolvePeriodBlocker = (
  isDirty: boolean,
  areLinesReady: boolean,
): string => {
  if (isDirty) return PERIOD_BLOCKED_BY_EDITS;
  if (!areLinesReady) return PERIOD_WAITING_FOR_LINES;
  return "";
};

const LOCK_NOTICE: Record<ReservedBookKind, string> = {
  face_price:
    "สมุดราคาหน้าซอง: แก้ราคาและส่วนลดที่นี่ไม่ได้ แก้ราคาหน้าซองที่หน้าสินค้า",
  sales_default: "สมุดราคานี้ถูกสงวนไว้โดยระบบ จึงแก้ไขราคาและรายการสินค้าไม่ได้",
};

interface PriceBookDetailsProps {
  priceBook: PriceBookDetail;
  /** May maintain the catalogue (marketing, admin, master). */
  canEdit: boolean;
  /** Which system-setting book this is, if any; its prices are read-only here. */
  reservedBook: ReservedBookKind | null;
  /** The system settings are still loading, so whether the book is reserved is not known yet. */
  settingsLoading: boolean;
  /** A draft compared with its source, for the draft bar; null on a live book or for a reader. */
  draftDiff: PricebookDraftDiffState | null;
  /** Something on the book was written, so its header has to be read again. */
  onChanged: () => void;
  /** Whether the book holds edits nobody saved yet; the header's copy and apply wait for them. */
  onDirtyChange: (isDirty: boolean) => void;
}

/**
 * The book's three summary cards and its tabs. Prices are edited in place and held here until
 * บันทึก; the branch dialog and the header drawer save on their own.
 */
export default function PriceBookDetails({
  priceBook,
  canEdit,
  reservedBook,
  settingsLoading,
  draftDiff,
  onChanged,
  onDirtyChange,
}: PriceBookDetailsProps) {
  const [searchParams] = useSearchParams();
  // The overview of every book's periods links straight to this book's ช่วงราคา tab.
  const [tab, setTab] = useState<DetailsTab>(() =>
    toDetailsTab(searchParams.get(PRICE_BOOK_TAB_PARAM)),
  );
  const [branchDialogOpen, setBranchDialogOpen] = useState<boolean>(false);

  const lockNotice = reservedBook ? LOCK_NOTICE[reservedBook] : "";

  const { branches, loading: branchesLoading, setBranches } =
    usePricebookBranches(priceBook.uuid);
  const editor = usePricebookEditor(priceBook.uuid, priceBook.discount_percent);
  // A save replaces the whole line set, so nothing is editable until every line is loaded.
  const canEditPrices =
    canEdit &&
    !settingsLoading &&
    !reservedBook &&
    !editor.loading &&
    !editor.loadFailed;
  const priceSave = usePricebookPriceSave({
    pricebookUuid: priceBook.uuid,
    storedBookDiscount: priceBook.discount_percent,
    editor,
    onSaved: onChanged,
  });
  const isDirty = editor.changeCount > 0;
  const leaveGuard = useLeavePageGuard(isDirty);

  // The face-price book takes no periods: its prices are the ราคาหน้าซอง and also value fines.
  const takesPeriods = !settingsLoading && reservedBook !== "face_price";
  const canManagePeriods = canEdit && takesPeriods;
  const periodDialog = usePeriodDialog(priceBook.uuid, editor.reload);
  const periodLines = useMemo(
    () =>
      editor.storedLines.flatMap((line): PeriodLine[] => {
        const periodLine = toPeriodLine(line);
        return periodLine ? [periodLine] : [];
      }),
    [editor.storedLines],
  );
  const periodBlocker = resolvePeriodBlocker(
    isDirty,
    !editor.loading && !editor.loadFailed,
  );
  const periodActions: LinePeriodActions | null = canManagePeriods
    ? {
        blocker: periodBlocker,
        onAdd: periodDialog.openCreate,
        onEdit: periodDialog.openEditById,
      }
    : null;
  // A draft is applied, never scheduled onto: only a live book has schedules.
  const takesSchedules = !priceBook.is_draft;
  const activeTab = resolveActiveTab(tab, takesPeriods, takesSchedules);

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  const impactMessage = buildPriceImpactMessage(
    branches,
    priceBook.branch_count,
    priceBook.active_store_count,
  );
  const isSaveBusy = priceSave.isPreviewing || priceSave.isSaving;
  const productTabLabel = `สินค้า (${numberWithCommas(editor.lines.length)})`;
  const shopTabLabel = `ร้านค้าตามสาขา (${numberWithCommas(
    priceBook.active_store_count,
  )})`;
  const scheduleTabLabel = `ตั้งเวลา (${numberWithCommas(
    priceBook.pending_schedules.length,
  )})`;
  const leaveDescription = `${numberWithCommas(
    editor.changeCount,
  )} การเปลี่ยนแปลงที่ยังไม่บันทึกจะหายไป`;
  const headOfficeBranchUuid = branches?.branches.find(
    (branch) => branch.is_head_office,
  )?.branch_uuid;

  const handleChangeTab = (_event: SyntheticEvent, value: string) =>
    setTab(toDetailsTab(value));
  const handleOpenSchedules = () => setTab(PRICE_BOOK_SCHEDULES_TAB);
  const handleOpenBranchDialog = () => setBranchDialogOpen(true);
  const handleCloseBranchDialog = () => setBranchDialogOpen(false);

  const handleBranchesSaved = (saved: PricebookBranches) => {
    setBranches(saved);
    onChanged();
  };

  // A period write stamps the book and changes its lines' chips and the expiring counts.
  const handlePeriodsChanged = () => {
    editor.reload();
    onChanged();
    notifyPricePeriodsChanged();
  };
  const handlePeriodSaved = () => {
    periodDialog.close();
    handlePeriodsChanged();
  };
  const handleAddPeriod = () => periodDialog.openCreate(null);

  return (
    <Stack gap={2}>
      {priceBook.is_draft && (
        <DraftBar
          sourceCode={priceBook.source_pricebook_code ?? ""}
          diffState={draftDiff}
          headOfficeBranchUuid={headOfficeBranchUuid}
        />
      )}
      <ScheduleBar
        pendingSchedules={priceBook.pending_schedules}
        onOpenSchedules={handleOpenSchedules}
      />
      <SummaryCards
        bookDiscountText={editor.bookDiscountText}
        isBookDiscountValid={editor.isBookDiscountValid}
        canEditBookDiscount={canEditPrices}
        onChangeBookDiscount={editor.changeBookDiscount}
        branches={branches}
        branchesLoading={branchesLoading}
        canChangeBranches={canEdit}
        onOpenBranchDialog={handleOpenBranchDialog}
      />

      {lockNotice && <Alert severity="info">{lockNotice}</Alert>}
      {priceSave.errorMessage && (
        <Alert severity="warning" onClose={priceSave.dismissError}>
          {priceSave.errorMessage}
        </Alert>
      )}
      {periodDialog.goneMessage && (
        <Alert severity="info" onClose={periodDialog.dismissGoneMessage}>
          {periodDialog.goneMessage}
        </Alert>
      )}

      <Paper>
        <Tabs
          value={activeTab}
          onChange={handleChangeTab}
          sx={{ mb: 2, borderBottom: (theme) => `1px solid ${theme.palette.draft.main}` }}
        >
          <Tab value="products" label={productTabLabel} />
          {takesPeriods && <Tab value={PRICE_BOOK_PERIODS_TAB} label="ช่วงราคา" />}
          {takesSchedules && (
            <Tab value={PRICE_BOOK_SCHEDULES_TAB} label={scheduleTabLabel} />
          )}
          <Tab value="shops" label={shopTabLabel} />
          <Tab value="history" label="ประวัติ" />
        </Tabs>
        {activeTab === "products" && (
          <PricebookProductTable
            editor={editor}
            canEdit={canEditPrices}
            periodActions={periodActions}
          />
        )}
        {activeTab === PRICE_BOOK_PERIODS_TAB && (
          <PeriodsTab
            key={priceBook.uuid}
            pricebookUuid={priceBook.uuid}
            updatedAt={priceBook.updated_at}
            canManage={canManagePeriods}
            blocker={periodBlocker}
            onAdd={handleAddPeriod}
            onEdit={periodDialog.openEdit}
            onChanged={handlePeriodsChanged}
          />
        )}
        {activeTab === PRICE_BOOK_SCHEDULES_TAB && (
          <SchedulesTab
            pricebookUuid={priceBook.uuid}
            canCancel={canEdit}
            onChanged={onChanged}
          />
        )}
        {activeTab === "shops" && (
          <BranchShopsTab
            pricebookUuid={priceBook.uuid}
            branches={branches}
            loading={branchesLoading}
            shopCount={priceBook.active_store_count}
          />
        )}
        {/* Every write moves `updated_at`, so the log is read again after one. */}
        {activeTab === "history" && (
          <HistoryTab
            key={priceBook.updated_at}
            pricebookUuid={priceBook.uuid}
            branches={branches}
          />
        )}
      </Paper>

      {isDirty && (
        <UnsavedChangesBar
          changeCount={editor.changeCount}
          busy={isSaveBusy}
          onDiscard={editor.discard}
          onSave={priceSave.requestSave}
        />
      )}

      <ConfirmPriceDialog
        rows={priceSave.changeRows}
        bookDiscountChange={priceSave.bookDiscountChange}
        codelessLineCount={editor.codelessLineCount}
        removedPeriodLineCount={priceSave.removedPeriodLineCount}
        impactMessage={impactMessage}
        saving={priceSave.isSaving}
        onClose={priceSave.cancelConfirm}
        onConfirm={priceSave.confirmSave}
      />

      <PeriodDialog
        target={periodDialog.target}
        pricebookUuid={priceBook.uuid}
        bookDiscountPercent={priceBook.discount_percent}
        lines={periodLines}
        onClose={periodDialog.close}
        onSaved={handlePeriodSaved}
      />

      <BranchDialog
        open={branchDialogOpen}
        pricebookUuid={priceBook.uuid}
        branches={branches}
        onClose={handleCloseBranchDialog}
        onSaved={handleBranchesSaved}
      />

      <ConfirmModal
        open={leaveGuard.isAskingToLeave}
        title="ออกจากหน้านี้โดยไม่บันทึก?"
        description={leaveDescription}
        cancelLabel="อยู่ต่อ"
        confirmLabel="ออกโดยไม่บันทึก"
        confirmColor="error"
        onClose={leaveGuard.stay}
        onConfirm={leaveGuard.leave}
      />
    </Stack>
  );
}
